import React, { useState, useEffect, useCallback } from 'react';
import { Star, TrendingUp, Award, ShoppingCart, Loader, Info } from 'lucide-react';
import { TOP_RATED_FOODS_URL, TRENDING_FOODS_URL, RECOMMENDED_FOODS_URL, CART_ADD_URL, CART_VIEW_URL } from '../../config/api';
import { authenticatedFetch, getFirstImageUrl } from '../Utils/apiHelpers';
import { trackFood, trackError } from '../Utils/analytics';
import FoodDetailModal from '../FoodDetailModal/FoodDetailModal';
import './FoodDiscovery.css';

type Tab = 'recommended' | 'top_rated' | 'trending';

interface DiscoveryFood {
  food_id: number;
  food_name: string;
  image_urls?: string | string[];
  food_image?: string;
  price?: number;
  average_rating?: number;
  total_reviews?: number;
  total_orders?: number;
  category?: string;
  description?: string;
  available_quantity?: number;
  recommendation_score?: number;
  kitchen?: { kitchen_id: number; kitchen_name: string };
}

interface FoodDiscoveryProps {
  onCartChange?: () => void;
  showNotification?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  locationVersion?: number;
}

const FALLBACK = 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg';

const FoodDiscovery: React.FC<FoodDiscoveryProps> = ({ onCartChange, showNotification, locationVersion = 0 }) => {
  const [activeTab, setActiveTab] = useState<Tab>('recommended');
  const [foods, setFoods] = useState<DiscoveryFood[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [detailFood, setDetailFood] = useState<DiscoveryFood | null>(null);
  const isLoggedIn = !!localStorage.getItem('authToken');

  const fetchFoods = useCallback(async (tab: Tab) => {
    setIsLoading(true);
    setError(null);
    setFoods([]);
    try {
      let url = '';
      if (tab === 'recommended') url = RECOMMENDED_FOODS_URL;
      else if (tab === 'top_rated') url = TOP_RATED_FOODS_URL;
      else url = TRENDING_FOODS_URL;

      const fetcher = tab === 'recommended' ? authenticatedFetch : fetch;
      const res = await fetcher(url, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list: DiscoveryFood[] = data.recommendations ?? data.foods ?? [];
      setFoods(list);
    } catch {
      setError('Could not load foods. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFoods(activeTab);
  }, [activeTab, fetchFoods, locationVersion]);

  useEffect(() => {
    const handler = () => fetchFoods(activeTab);
    window.addEventListener('foodAvailabilityChanged', handler);
    return () => window.removeEventListener('foodAvailabilityChanged', handler);
  }, [activeTab, fetchFoods]);

  const handleAddToCart = async (food: DiscoveryFood) => {
    if (!isLoggedIn) {
      showNotification?.('info', 'Login Required', 'Please log in to add items to your cart.');
      return;
    }
    setAddingId(food.food_id);
    try {
      // Check existing cart kitchen
      const cartRes = await authenticatedFetch(CART_VIEW_URL);
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        if (cartData.success && cartData.items?.length > 0) {
          const existingKitchen = cartData.items[0]?.kitchen_id;
          if (existingKitchen && food.kitchen?.kitchen_id && existingKitchen !== food.kitchen.kitchen_id) {
            showNotification?.('warning', 'Different Kitchen', 'Clear your cart first to order from a different kitchen.');
            setAddingId(null);
            return;
          }
        }
      }

      const res = await authenticatedFetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ food_id: food.food_id, quantity: 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        showNotification?.('success', 'Added to Cart', `${food.food_name} added to your cart.`);
        trackFood('add_to_cart', food.food_name);
        onCartChange?.();
      } else {
        showNotification?.('error', 'Failed', data.message || 'Could not add to cart.');
        trackError('cart_add_failed', 'food_discovery');
      }
    } catch {
      showNotification?.('error', 'Network Error', 'Please check your connection.');
    } finally {
      setAddingId(null);
    }
  };

  const getImageUrl = (food: DiscoveryFood): string => {
    if (food.food_image) return getFirstImageUrl(food.food_image);
    return getFirstImageUrl(food.image_urls);
  };

  const tabs: { key: Tab; label: string; Icon: React.ElementType }[] = [
    { key: 'recommended', label: 'For You', Icon: Award },
    { key: 'top_rated', label: 'Top Rated', Icon: Star },
    { key: 'trending', label: 'Trending', Icon: TrendingUp },
  ];

  return (
    <section className="fd-section">
      <div className="fd-header">
        <div className="fd-title-group">
          <h2 className="fd-title">Discover Food</h2>
          <p className="fd-subtitle">Personalized picks, fan favorites, and what's hot right now</p>
        </div>
        <div className="fd-tabs">
          {tabs.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`fd-tab${activeTab === key ? ' fd-tab--active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="fd-loading">
          <div className="fd-spinner" />
          <span>Loading...</span>
        </div>
      )}

      {error && !isLoading && (
        <div className="fd-error">{error}</div>
      )}

      {!isLoading && !error && foods.length === 0 && (
        <div className="fd-empty">No foods available right now. Check back soon!</div>
      )}

      {!isLoading && !error && foods.length > 0 && (
        <div className="fd-grid">
          {foods.map(food => {
            const imgUrl = getImageUrl(food);
            const isAdding = addingId === food.food_id;

            return (
              <div key={food.food_id} className="fd-card">
                <div className="fd-card-img-wrap">
                  <img
                    src={imgUrl}
                    alt={food.food_name}
                    className="fd-card-img"
                    onError={e => { (e.target as HTMLImageElement).src = FALLBACK; }}
                  />
                  {food.average_rating !== undefined && food.average_rating > 0 && (
                    <span className="fd-rating-badge">
                      <Star size={11} fill="currentColor" />
                      {food.average_rating.toFixed(1)}
                      {food.total_reviews !== undefined && food.total_reviews > 0 && (
                        <span className="fd-reviews"> ({food.total_reviews})</span>
                      )}
                    </span>
                  )}
                  {activeTab === 'trending' && food.total_orders !== undefined && food.total_orders > 0 && (
                    <span className="fd-trending-badge">
                      <TrendingUp size={11} />
                      {food.total_orders} orders
                    </span>
                  )}
                </div>

                <div className="fd-card-body">
                  <h4 className="fd-card-name">{food.food_name}</h4>
                  {food.kitchen && (
                    <p className="fd-card-kitchen">by {food.kitchen.kitchen_name}</p>
                  )}
                  {food.description && (
                    <p className="fd-card-desc">{food.description}</p>
                  )}
                  <div className="fd-card-footer">
                    {food.price !== undefined && (
                      <span className="fd-price">₹{food.price}</span>
                    )}
                    <div className="fd-card-actions">
                      <button
                        className="fd-info-btn"
                        onClick={() => setDetailFood(food)}
                        title="View details & reviews"
                      >
                        <Info size={14} />
                      </button>
                      {isLoggedIn && food.price !== undefined && (
                        <button
                          className="fd-add-btn"
                          onClick={() => handleAddToCart(food)}
                          disabled={isAdding}
                        >
                          {isAdding
                            ? <Loader size={14} className="fd-add-spinner" />
                            : <ShoppingCart size={14} />}
                          {isAdding ? 'Adding...' : 'Add'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {detailFood && (
        <FoodDetailModal
          foodId={detailFood.food_id}
          onClose={() => setDetailFood(null)}
          showNotification={showNotification}
          onCartChange={onCartChange}
        />
      )}
    </section>
  );
};

export default FoodDiscovery;
